import React, { useState, useEffect } from "react";
import {
  X,
  MapPin,
  Mail,
  Phone,
  Briefcase,
  Calendar,
  IndianRupee,
  TrendingUp,
  Clock,
  Plane,
  User,
  Activity,
  GraduationCap,
  Building2,
  FileText,
  CalendarCheck,
  MessageCircle,
  ChevronUp,
  ChevronDown,
  Layers,
  Map,
} from "lucide-react";
import { handleOutsideClick } from "../../../Helpers/HandleOutsideClick";

const CandidateDetailsModal = ({
  show,
  candidateDetails,
  selectedCandidate,
  onClose,
  onViewJD,
  onViewResume,
  onViewSystemRemark,
  showResumeButton = false,
  showSystemRemarkButton = false,
}) => {
  const [isJobOpen, setIsJobOpen] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(show);

  useEffect(() => {
    if (show) {
      document.body.style.overflow = "hidden";
      setShouldRender(true);
      setTimeout(() => setIsVisible(true), 10);
    } else {
      document.body.style.overflow = "auto";
      setIsVisible(false);
      setTimeout(() => setShouldRender(false), 400);
    }
  }, [show]);

  if (!shouldRender || !candidateDetails) return null;

  return (
    <div className="fixed inset-0 z-50">
      {/* Overlay */}
      <div
        onClick={onClose}
        className={`fixed inset-0 transition-opacity duration-300 ${
          isVisible ? "bg-black/40 opacity-100" : "bg-black/0 opacity-0"
        }`}
      />

      {/* RIGHT SIDE DRAWER */}
      <div className="fixed inset-0 flex items-end md:items-stretch justify-center md:justify-end">
        <div
          onClick={(e) => e.stopPropagation()}
          className={`bg-white h-full w-full md:w-[45%] rounded-t-2xl md:rounded-none shadow-xl transform transition-transform duration-300 ${
            isVisible ? "translate-x-0" : "translate-x-full"
          }`}
        >
          {/*  SAME UI */}
          <div className="flex justify-between items-center px-4 sm:px-6 py-3 sm:py-4 border-b">
            <h2 className="text-xl font-semibold">Candidate Details</h2>
            <button onClick={onClose}>
              <X size={22} />
            </button>
          </div>

          <div className="p-4 sm:p-6 space-y-5 sm:space-y-6 overflow-y-auto h-[calc(100vh-70px)] md:h-[calc(100vh-80px)]">
            {/* PERSONAL INFO */}
            <div className="bg-gray-50 rounded-xl p-6 shadow-sm">
              <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3">
                {/* LEFT SECTION */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                  <h3 className="text-base sm:text-lg font-semibold text-blue-600 uppercase break-words">
                    {candidateDetails?.candidateDetails?.name || "-NA-"}
                  </h3>

                  {selectedCandidate?.status && (
                    <span className="w-fit flex items-center gap-2 px-3 py-1 text-xs sm:text-sm font-medium bg-primary text-white rounded-full shadow">
                      <Activity size={14} className="sm:w-4 sm:h-4" />
                      {selectedCandidate?.status}
                    </span>
                  )}
                </div>

                {/* RIGHT SECTION */}
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-2 w-full md:w-auto">
                  {showResumeButton && (
                    <button
                      onClick={onViewResume}
                      className="bg-primary text-white px-4 py-2 sm:py-1 rounded-md text-sm w-full sm:w-auto"
                    >
                      View Resume
                    </button>
                  )}

                  {showSystemRemarkButton && (
                    <button
                      onClick={onViewSystemRemark}
                      className="bg-gradient-to-r from-blue-800 to-blue-400 text-white px-4 py-2 sm:py-1 rounded-md text-sm w-full sm:w-auto"
                    >
                      System Remark
                    </button>
                  )}
                </div>
              </div>
              <hr className="my-4" />

              {/* DETAILS GRID */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 text-sm">
                <Info
                  icon={<Mail size={16} className="text-primary" />}
                  label="Email"
                  value={candidateDetails?.candidateDetails?.email}
                />
                <Info
                  icon={<Phone size={16} className="text-primary" />}
                  label="Mobile"
                  value={candidateDetails?.candidateDetails?.mobile}
                />
                <Info
                  icon={<MapPin size={16} className="text-primary" />}
                  label="Location"
                  value={candidateDetails?.candidateDetails?.currentLocation}
                />
                <Info
                  icon={<MapPin size={16} className="text-primary" />}
                  label="Preferred Location"
                  value={
                    candidateDetails?.candidateDetails?.preferredLocation ||
                    "N/A"
                  }
                />
                <Info
                  icon={<Briefcase size={16} className="text-primary" />}
                  label="Total Experience (in years)"
                  value={
                    candidateDetails?.candidateDetails?.totalExperience || 0
                  }
                />

                <Info
                  icon={<Briefcase size={16} className="text-primary" />}
                  label="Domain Experience (in years)"
                  value={
                    candidateDetails?.candidateDetails?.domainExperience || 0
                  }
                />
                <Info
                  icon={<IndianRupee size={16} className="text-primary" />}
                  label="Current CTC"
                  value={candidateDetails?.candidateDetails?.currentCTC || 0}
                />
                <Info
                  icon={<TrendingUp size={16} className="text-primary" />}
                  label="Expected CTC"
                  value={candidateDetails?.candidateDetails?.expectedCTC || 0}
                />
                <Info
                  icon={<User size={16} className="text-primary" />}
                  label="Gender"
                  value={candidateDetails?.candidateDetails?.gender || "N/A"}
                />
                <Info
                  icon={<GraduationCap size={16} className="text-primary" />}
                  label="Qualification"
                  value={
                    candidateDetails?.candidateDetails?.qualification || "N/A"
                  }
                />

                <Info
                  icon={<Building2 size={16} className="text-primary" />}
                  label="Current Company"
                  value={
                    candidateDetails?.candidateDetails?.currentCompany || "N/A"
                  }
                />

                <Info
                  icon={<FileText size={16} className="text-primary" />}
                  label="Remarks"
                  value={candidateDetails?.candidateDetails?.remarks || "N/A"}
                />
                <Info
                  icon={<CalendarCheck size={16} className="text-primary" />}
                  label="Notice Period"
                  value={`${candidateDetails?.candidateDetails?.noticePeriod || 0} days`}
                />
                <Info
                  icon={<Plane size={16} className="text-primary" />}
                  label="Ready to Relocate"
                  value={candidateDetails?.candidateDetails?.readytoRelocate}
                />
                <Info
                  icon={<MessageCircle size={16} className="text-primary" />}
                  label="Reason"
                  value={candidateDetails?.candidateDetails?.reasonofLeaving}
                />
              </div>
            </div>

            {/* JOB DETAILS */}
            <div className="bg-gray-50 rounded-xl shadow-sm">
              <div className="flex justify-between items-center px-6 py-4">
                <h3 className="text-lg font-semibold text-blue-600 uppercase">
                  {candidateDetails?.jobDetails?.jobTitel || "-NA-"}
                </h3>

                <button
                  onClick={() => setIsJobOpen(!isJobOpen)}
                  className="border border-blue-500 p-1 rounded-md"
                >
                  {isJobOpen ? (
                    <ChevronUp size={18} />
                  ) : (
                    <ChevronDown size={18} />
                  )}
                </button>
              </div>

              {isJobOpen && (
                <div className="px-4 sm:px-6 pb-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 text-sm">
                  <Info
                    icon={<Briefcase size={16} className="text-primary" />}
                    label="Function"
                    value={candidateDetails.jobDetails?.functionName}
                  />

                  <Info
                    icon={<Building2 size={16} className="text-primary" />}
                    label="Department"
                    value={candidateDetails.jobDetails?.department}
                  />

                  <Info
                    icon={<Layers size={16} className="text-primary" />}
                    label="Grade"
                    value={candidateDetails.jobDetails?.grade}
                  />

                  <Info
                    icon={<MapPin size={16} className="text-primary" />}
                    label="Location"
                    value={candidateDetails.jobDetails?.location}
                  />

                  <Info
                    icon={<Map size={16} className="text-primary" />}
                    label="Zone"
                    value={candidateDetails.jobDetails?.zone}
                  />

                  <Info
                    icon={<IndianRupee size={16} className="text-primary" />}
                    label="Offered Salary"
                    value={`₹ ${candidateDetails.jobDetails?.offeredSalary}`}
                  />

                  <Info
                    icon={<GraduationCap size={16} className="text-primary" />}
                    label="Qualification"
                    value={candidateDetails.jobDetails?.minimumQualification}
                  />

                  <Info
                    icon={<TrendingUp size={16} className="text-primary" />}
                    label="Min Experience (in years)"
                    value={candidateDetails.jobDetails?.minimumExperience}
                  />

                  <Info
                    icon={<User size={16} className="text-primary" />}
                    label="Gender"
                    value={candidateDetails.jobDetails?.genderPreference}
                  />
                </div>
              )}
            </div>

            {/* DESCRIPTION */}
            <div className="bg-gray-50 rounded-xl shadow-sm p-5">
              <h3 className="text-lg font-semibold text-primary mb-3">
                Description
              </h3>

              <button
                onClick={onViewJD}
                className="border border-primary text-primary px-4 py-1 rounded-md text-sm hover:bg-primary hover:text-white transition"
              >
                View Job Description
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Info = ({ icon, label, value }) => {
  return (
    <div className="flex items-start gap-3 min-w-0">
      {/* Icon */}
      <div className="mt-1">{icon}</div>

      {/* Content */}
      <div className="min-w-0">
        <p className="text-gray-400 text-xs">{label}</p>

        {/* Value */}
        <p
          className="text-gray-800 text-sm font-medium break-words md:truncate md:max-w-[280px]"
          title={value}
        >
          {value || "N/A"}
        </p>
      </div>
    </div>
  );
};

export default CandidateDetailsModal;
